<script lang="ts">
	import {
		Alert,
		Checkbox,
		Col,
		Container,
		Row,
		Stack,
		Text,
		WizardContainer,
		WizardContent,
	} from '@Shelly/shelly-design-system/index';
	import { clonableSettings } from 'src/helpers/settings/settings_config_helpers';
	import { format } from 'svelte-i18n';
	import SelectList from '../SelectList/SelectList.svelte';
	import { handleDeviceSelection } from 'src/helpers/settings/wizard_settings_config_helpers';
	import type { Selection } from 'src/types/Device';

	const WIZARD_STEPS = {
		INTRODUCTION: 0,
		DEVICE_SELECTION: 1,
		TEMPLATE_SELECTION: 2,
	};

	export let activeStep = { activeIndex: 0 };
	export let wizardItems = [];
	export let selected: Selection = { id: '', type: '' };
	export let compatibleDevices = [];
	export let compatibleDevicesList = [];
	export let allDevicesRefs: { [key: string | number]: HTMLElement } = {};
	export let isBulkMode = false;
	export let isMultipleDevices = false;
	export let selectedDevices = [];
	export let existingTemplates = [];

	export let selectedSettingsKeys: string[] = clonableSettings().map((s) => s.key);

	const toggleSetting = (key: string) => {
		if (selectedSettingsKeys.includes(key)) {
			selectedSettingsKeys = selectedSettingsKeys.filter((k) => k !== key);
		} else {
			selectedSettingsKeys = [...selectedSettingsKeys, key];
		}
	};
</script>

<WizardContainer activeStepIndex={activeStep.activeIndex} adaptiveHeight>
	{#each wizardItems as item, i (i)}
		<WizardContent active={activeStep.activeIndex === i}>
			{#if i === WIZARD_STEPS.INTRODUCTION}
				<!-- Introduction Step -->
				<Stack gap="m">
					{#if isBulkMode && isMultipleDevices}
						<Alert
							type="info"
							heading={$format('text_template_bulk_mode_heading', {
								default: 'Bulk Mode',
							})}
							message={$format('text_template_bulk_mode_message', {
								default: `You are applying settings to ${selectedDevices.length} selected devices.`,
								values: {
									selectedDevicesLength: selectedDevices.length,
								},
							})}
							class="mb-m"
						/>
					{/if}
					<Text class="mb-m">
						{$format('text_template_introduction_description', {
							default:
								'You can apply the following settings either from existing device or pre-saved settings template.',
						})}
					</Text>
					<Text element="h3" class="mb-s">
						{$format('text_template_introduction_available_settings', {
							default: 'Available settings for cloning:',
						})}
					</Text>
					<Stack gap="s" class="mb-m">
						{#each clonableSettings() as setting}
							<Checkbox
								id={setting.key}
								size="m"
								checked={selectedSettingsKeys.includes(setting.key)}
								on:click={() => toggleSetting(setting.key)}
							>
								{setting.title}
							</Checkbox>
						{/each}
					</Stack>
				</Stack>
			{:else if i === WIZARD_STEPS.DEVICE_SELECTION}
				<!-- Device Selection Step -->
				<Stack gap="m">
					<Text element="h2" class="mb-m">
						{$format('text_template_device_selection_heading', {
							default: 'Select configuration from existing device',
						})}
					</Text>
					{#if compatibleDevices.length > 0}
						<Container noScroll padding="scrollPaddings">
							<SelectList
								list={compatibleDevicesList}
								variant="radio"
								selectedCardsId={selected.type === 'device' ? [{ id: selected.id }] : []}
								bind:allCardsRefs={allDevicesRefs}
								on:click={(e) => (selected = handleDeviceSelection(e, selected))}
							/>
						</Container>
					{:else}
						<Alert
							type="info"
							heading={$format('text_no_compatible_devices_heading', {
								default: 'No Compatible Devices',
							})}
							message={$format('text_no_compatible_devices_message', {
								default:
									'No compatible devices found. You can proceed to the next step to select from templates.',
							})}
							class="mb-s"
						/>
					{/if}
				</Stack>
			{:else if i === WIZARD_STEPS.TEMPLATE_SELECTION}
				<!-- Template Selection Step -->
				<Stack gap="m">
					<Text element="h2" class="mb-m">
						{$format('text_template_template_selection_heading', {
							default: 'Select configuration from pre-saved template',
						})}
					</Text>
					{#if existingTemplates.length > 0}
						{#each existingTemplates as template}
							<Row class="mb-s">
								<Col sm="10" md="11">
									<Checkbox
										id={template.id}
										size="m"
										checked={selected.type === 'template' &&
											selected.id === template.id}
										on:click={() =>
											(selected =
												selected.type === 'template' &&
												selected.id === template.id
													? { id: '', type: '' }
													: { id: template.id, type: 'template' })}
									>
										{template.name}
										{#if template.notes}
											<Text styleType="body-small" class="text-color-muted"
												>- {template.notes}</Text
											>
										{/if}
									</Checkbox>
								</Col>
							</Row>
						{/each}
					{:else}
						<Alert
							type="info"
							heading={$format('no_templates_heading', {
								default: 'No Templates Available',
							})}
							message={$format('no_templates_message', {
								default: 'No saved templates found.',
							})}
						/>
					{/if}
				</Stack>
			{/if}
		</WizardContent>
	{/each}
</WizardContainer>
